import { Firestore, addDoc, collection, serverTimestamp } from '@angular/fire/firestore';
import { AdvancedDebugging } from './advanced-debugging.utilities';

export interface EmailMessage {
	to: string | string[];
	cc?: string | string[];
	bcc?: string | string[];
	message: {
		subject?: string;
		text?: string;
		html?: string;
		sendgridTemplateId?: string;
		sendgridTemplateData?: Record<string, unknown>;
	};
}

export type MasEmailPayloadValidationResult =
	| { ok: true; payload: Record<string, unknown> }
	| { ok: false; error: string };

function isNonEmptyString(value: unknown): value is string {
	return typeof value === 'string' && value.trim().length > 0;
}

function normalizeEmailList(value: unknown): string[] | null {
	if (typeof value === 'string' && value.trim()) {
		return [value.trim()];
	}

	if (Array.isArray(value)) {
		const emails = value.filter(isNonEmptyString);
		if (emails.length === value.length && emails.length > 0) {
			return emails;
		}
	}

	return null;
}

/** Parse editable JSON and validate a mas-email Firestore payload before send. */
export function parseAndValidateMasEmailPayload(jsonText: string): MasEmailPayloadValidationResult {
	let parsed: unknown;

	try {
		parsed = JSON.parse(jsonText);
	} catch (error) {
		return {
			ok: false,
			error: error instanceof Error ? error.message : 'Invalid JSON',
		};
	}

	if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
		return { ok: false, error: 'Payload must be a JSON object' };
	}

	const payload = parsed as Record<string, unknown>;
	const to = normalizeEmailList(payload['to']);
	if (!to) {
		return { ok: false, error: '"to" is required (string or non-empty string array)' };
	}

	const messageRaw = payload['message'];
	const message =
		messageRaw && typeof messageRaw === 'object' && !Array.isArray(messageRaw)
			? (messageRaw as Record<string, unknown>)
			: undefined;

	const sendgridTemplateId = isNonEmptyString(message?.['sendgridTemplateId'])
		? message['sendgridTemplateId'].trim()
		: isNonEmptyString(payload['sendgridTemplateId'])
			? payload['sendgridTemplateId'].trim()
			: '';

	const subject = isNonEmptyString(message?.['subject'])
		? message['subject'].trim()
		: isNonEmptyString(payload['subject'])
			? payload['subject'].trim()
			: '';

	const text = isNonEmptyString(message?.['text']) ? message['text'] : isNonEmptyString(payload['text']) ? payload['text'] : '';
	const html = isNonEmptyString(message?.['html']) ? message['html'] : isNonEmptyString(payload['html']) ? payload['html'] : '';

	if (sendgridTemplateId) {
		const templateData = message?.['sendgridTemplateData'];
		if (templateData != null && (typeof templateData !== 'object' || Array.isArray(templateData))) {
			return { ok: false, error: 'message.sendgridTemplateData must be an object' };
		}

		return {
			ok: true,
			payload: {
				...payload,
				to,
				message: {
					...message,
					sendgridTemplateId,
				},
			},
		};
	}

	if (!subject && !text && !html) {
		return {
			ok: false,
			error: 'Provide message.sendgridTemplateId or a subject with text/html content',
		};
	}

	return {
		ok: true,
		payload: {
			...payload,
			to,
		},
	};
}

export class SendEmail {
	private static readonly opWrite = 'app:SendEmail.writeEmailToFirestore';
	private static readonly opAddDoc = 'app:SendEmail.addDoc';
	private static readonly opMissingSubject = 'app:SendEmail.missingSubject';

	private readonly advancedDebugging = new AdvancedDebugging();

	constructor(private firestore: Firestore) {}

	private normalizeEmails(input: string | string[] | undefined): string[] | undefined {
		if (typeof input === 'string') {
			return input
				.split(',')
				.map(email => email.trim())
				.filter(email => email !== '');
		}
		return input;
	}

	private cleanUndefinedFields(obj: any): any {
		const cleaned: any = {};
		for (const key in obj) {
			if (obj[key] !== undefined) {
				cleaned[key] = obj[key];
			}
		}
		return cleaned;
	}

	private emailLogContext(message: EmailMessage): { subject?: string; toCount: number; hasCc: boolean; hasBcc: boolean } {
		const to = this.normalizeEmails(message.to) ?? [];
		return {
			subject: message.message?.subject,
			toCount: to.length,
			hasCc: !!message.cc,
			hasBcc: !!message.bcc,
		};
	}

	async writeMasEmailPayload(payload: Record<string, unknown>): Promise<string> {
		const validation = parseAndValidateMasEmailPayload(JSON.stringify(payload));
		if (!validation.ok) {
			throw new Error(validation.error);
		}

		return this.writeValidatedMasEmailPayload(validation.payload);
	}

	async writeMasEmailJson(jsonText: string): Promise<string> {
		const validation = parseAndValidateMasEmailPayload(jsonText);
		if (!validation.ok) {
			throw new Error(validation.error);
		}

		return this.writeValidatedMasEmailPayload(validation.payload);
	}

	private async writeValidatedMasEmailPayload(docPayload: Record<string, unknown>): Promise<string> {
		try {
			if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
				const toField = docPayload['to'];
				this.advancedDebugging.log(SendEmail.opWrite, { toCount: Array.isArray(toField) ? toField.length : 1 });
			}

			const ref = collection(this.firestore, 'mas-email');
			const docRef = await addDoc(ref, {
				...docPayload,
				createdAt: serverTimestamp(),
				source: 'mas-sendgrid-test',
			}).catch(err => {
				this.advancedDebugging.captureError(SendEmail.opAddDoc, err, {
					tags: { operation: SendEmail.opAddDoc },
				});
				throw err;
			});
			return docRef.id;
		} catch (error) {
			this.advancedDebugging.captureError(SendEmail.opWrite, error, {
				tags: { operation: SendEmail.opWrite },
			});
			throw error;
		}
	}

	async writeEmailToFirestore(message: EmailMessage) {
		try {
			const inner = message.message;
			const sendgridTemplateId = inner?.sendgridTemplateId?.trim();

			if (!sendgridTemplateId && !inner?.subject) {
				this.advancedDebugging.captureError(SendEmail.opMissingSubject, new Error('message.message.subject is required'), {
					tags: { operation: SendEmail.opMissingSubject },
					...this.emailLogContext(message),
				});
				return;
			}

			if (sendgridTemplateId) {
				await this.writeValidatedMasEmailPayload(
					this.cleanUndefinedFields({
						to: this.normalizeEmails(message.to) ?? [],
						cc: this.normalizeEmails(message.cc),
						bcc: this.normalizeEmails(message.bcc),
						message: this.cleanUndefinedFields({
							sendgridTemplateId,
							sendgridTemplateData: inner?.sendgridTemplateData,
						}),
					}),
				);
				return;
			}

			const docPayload = this.cleanUndefinedFields({
				to: this.normalizeEmails(message.to) ?? [],
				cc: this.normalizeEmails(message.cc),
				bcc: this.normalizeEmails(message.bcc),
				subject: inner!.subject,
				text: inner!.text,
				html: inner!.html,
			});

			if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
				this.advancedDebugging.log(SendEmail.opWrite, this.emailLogContext(message));
			}

			const ref = collection(this.firestore, 'mas-email');
			await addDoc(ref, docPayload).catch(err => {
				this.advancedDebugging.captureError(SendEmail.opAddDoc, err, {
					tags: { operation: SendEmail.opAddDoc },
					...this.emailLogContext(message),
				});
			});
		} catch (error) {
			this.advancedDebugging.captureError(SendEmail.opWrite, error, {
				tags: { operation: SendEmail.opWrite },
				...this.emailLogContext(message),
			});
		}
	}
}
